import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { SensorAlertBundle } from '@mark43/cad-api';
import {
    Box,
    Button,
    ButtonGroup,
    Drawer,
    DrawerHeader,
    EmptyState,
    Flex,
    Text,
} from '@cad-arc';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { getSensorAlertSortTime } from './helpers';
import { SensorEventCard, SensorEventCardActions } from './sensor-event-card';
import { useArchiveSensorAlertsWithUndo } from './use-archive-sensor-alert';
import { useSensorsStore } from './use-sensors-store';

type SensorEventsTab = 'unread' | 'archived';

/** Past this, the list counts as scrolled away from the newest events. */
const SCROLLED_AWAY_PX = 40;

/**
 * Tracks whether the list is scrolled away from the top, and how many unread events arrived
 * since it was. New events land at the top; the browser's scroll anchoring already keeps the view
 * from jumping, so this only has to say that something arrived.
 */
function useNewEventsSinceScrolledAway(unreadAlerts: SensorAlertBundle[]) {
    const [isScrolledAway, setIsScrolledAway] = useState(false);
    const newestUnreadTime = unreadAlerts.length > 0 ? getSensorAlertSortTime(unreadAlerts[0]) : 0;
    const newestSeenTimeRef = useRef(newestUnreadTime);

    useEffect(
        function markEverythingSeenWhileAtTop() {
            if (!isScrolledAway) {
                newestSeenTimeRef.current = newestUnreadTime;
            }
        },
        [isScrolledAway, newestUnreadTime]
    );

    const newEventCount = isScrolledAway
        ? unreadAlerts.filter((alert) => getSensorAlertSortTime(alert) > newestSeenTimeRef.current)
              .length
        : 0;

    return { newEventCount, setIsScrolledAway };
}

/**
 * The Sensor Events drawer: every camera's events, newest first, split into Unread and Archived.
 *
 * **Read and archived are the same state.** There is no separate read flag anywhere: Unread lists
 * what is not archived, Archived what is. Archive state is per user and server-side, so it
 * survives a reload and follows the dispatcher between windows.
 *
 * Arc's drawer and controls as they come. The design handoff styles the shell and the controls its
 * own way; where that disagrees with Arc, Arc wins.
 */
export const SensorEventsDrawer = observer(function SensorEventsDrawer(): React.ReactElement {
    const drawerControls = useDrawerControls();
    const sensorsStore = useSensorsStore();
    const archiveWithUndo = useArchiveSensorAlertsWithUndo();
    const listRef = useRef<HTMLDivElement>(null);
    const [tab, setTab] = useState<SensorEventsTab>('unread');
    const [selectedAlertKey, setSelectedAlertKey] = useState<string>();

    const unreadAlerts = sensorsStore.getAlerts({ archived: false });
    const archivedAlerts = sensorsStore.getAlerts({ archived: true });
    const isArchivedTab = tab === 'archived';
    const alerts = isArchivedTab ? archivedAlerts : unreadAlerts;
    const { newEventCount, setIsScrolledAway } = useNewEventsSinceScrolledAway(unreadAlerts);

    function scrollToTop(behavior: ScrollBehavior) {
        listRef.current?.scrollTo({ top: 0, behavior });
    }

    function switchTab(nextTab: SensorEventsTab) {
        setTab(nextTab);
        scrollToTop('auto');
        setIsScrolledAway(false);
    }

    return (
        <Drawer isOpen size="lg" onClose={() => drawerControls.close()}>
            {/* No close button here: Arc's DrawerContent always renders its own. */}
            <DrawerHeader>Sensor events ({alerts.length})</DrawerHeader>

            <Flex
                alignItems="center"
                gap={2}
                paddingX={4}
                paddingY={2}
                flexShrink={0}
                borderBottom="sm"
                borderColor="border.default"
            >
                {/* The same segmented control as the grid panel's rail. */}
                <ButtonGroup isAttached size="sm">
                    <Button
                        variant={isArchivedTab ? 'outline' : 'solid'}
                        aria-pressed={!isArchivedTab}
                        onClick={() => switchTab('unread')}
                    >
                        Unread {unreadAlerts.length}
                    </Button>
                    <Button
                        variant={isArchivedTab ? 'solid' : 'outline'}
                        aria-pressed={isArchivedTab}
                        onClick={() => switchTab('archived')}
                    >
                        Archived {archivedAlerts.length}
                    </Button>
                </ButtonGroup>
                <Box flex="1" />
                {/* One request for the lot, with an undo — see `useArchiveSensorAlerts`. */}
                <Button
                    size="sm"
                    variant="ghost"
                    leadingVisual={isArchivedTab ? 'Unarchive' : 'Archive'}
                    isDisabled={alerts.length === 0}
                    onClick={() => archiveWithUndo(alerts, !isArchivedTab)}
                >
                    {isArchivedTab ? 'Restore all' : 'Archive all'}
                </Button>
            </Flex>

            <Box position="relative" flex="1 1 auto" minHeight={0} display="flex">
                {/* A plain Box rather than DrawerBody, which is only a padded scroller and does not
                    take the ref or the scroll handler this needs. */}
                <Box
                    ref={listRef}
                    flex="1 1 auto"
                    minHeight={0}
                    overflowY="auto"
                    onScroll={(event: React.UIEvent<HTMLDivElement>) =>
                        setIsScrolledAway(event.currentTarget.scrollTop > SCROLLED_AWAY_PX)
                    }
                >
                    {alerts.length === 0 ? (
                        <Box paddingTop={10}>
                            <EmptyState
                                visual={isArchivedTab ? 'Archive' : 'Camera'}
                                title={
                                    isArchivedTab
                                        ? 'No archived sensor events'
                                        : 'No unread sensor events'
                                }
                                subtitle={
                                    isArchivedTab
                                        ? 'Events you archive appear here.'
                                        : 'New events appear here as sensors report them.'
                                }
                            />
                        </Box>
                    ) : (
                        <Box as="ul" margin={0} padding={0} data-testid="SENSOR_EVENTS_LIST">
                            {alerts.map((alert) => {
                                const alertKey = alert.alertKey;
                                return (
                                    <SensorEventCard
                                        key={alertKey}
                                        alert={alert}
                                        isSelected={selectedAlertKey === alertKey}
                                        onSelect={() => setSelectedAlertKey(alertKey)}
                                    >
                                        <SensorEventCardActions
                                            alert={alert}
                                            isArchived={isArchivedTab}
                                            onArchive={(selected, archived) =>
                                                archiveWithUndo([selected], archived)
                                            }
                                        />
                                    </SensorEventCard>
                                );
                            })}
                            {/* Surfaced rather than swallowed: a list that silently stops short
                                reads as "that is all there was". */}
                            {sensorsStore.isAlertHistoryTruncated ? (
                                <Box as="li" listStyleType="none" padding={4} textAlign="center">
                                    <Text variant="caption" color="tertiary">
                                        Older events are not shown
                                    </Text>
                                </Box>
                            ) : undefined}
                        </Box>
                    )}
                </Box>

                {/* Outside the scroller, so scroll anchoring never picks it as its anchor. */}
                {!isArchivedTab && newEventCount > 0 ? (
                    <Box position="absolute" top={2} left="50%" transform="translateX(-50%)">
                        <Button
                            size="sm"
                            variant="solid"
                            leadingVisual="ArrowUp"
                            onClick={() => scrollToTop('smooth')}
                        >
                            {newEventCount === 1 ? '1 new event' : `${newEventCount} new events`}
                        </Button>
                    </Box>
                ) : undefined}
            </Box>
        </Drawer>
    );
});

export type SensorEventsDrawerType = typeof SensorEventsDrawer;
